import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Box, Typography } from '@qbe/ads-core';
import React, { useState } from 'react';

import SelectedPortableItem from '~/components/SelectedPortableItem';
import UpdateSpecifiedItemModal from '~/components/UpdateSpecifiedItemModal';
import LinkButton from '~/components-ds/LinkButton';
import {
  SPECIFIED_ITEM_LIMIT,
  TOTAL_COVER_VALUE_LIMIT,
  TOTAL_SPECIFIED_ITEMS_LIMIT,
} from '~/constants/domain-specific';
import { useAppContent, useAppDispatch, useBusinessDetails, useCoverDetails } from '~/hooks';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { updateSpecifiedItems } from '~/slices/coverDetails';
import { qalFormStartAction, qalTrackEngagementAction } from '~/thunks/analytics';
import { CoverItem, CoverTypes } from '~/types/cover';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';

const serialNumber = CoverTypes.SPECIFIED;

const SpecifiedItems: React.FC = () => {
  const dispatch = useAppDispatch();
  const { industry } = useBusinessDetails();
  const { specifiedItems, totalCoverValue } = useCoverDetails();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CoverItem>();

  const specifiedItemLimit = industry?.limits?.specifiedItemMin || SPECIFIED_ITEM_LIMIT;
  const totalCoverValueLimit =
    industry?.limits?.totalPortableItemSumInsured || TOTAL_COVER_VALUE_LIMIT;
  const isMaxLimitError = totalCoverValue !== null && totalCoverValue > totalCoverValueLimit;

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  const handleDelete = (id: string) => {
    if (specifiedItems) {
      const updatedList = [...specifiedItems].filter((item) => item.id !== id);
      dispatch(updateSpecifiedItems(updatedList));
    }
  };

  const handleEdit = (item: CoverItem) => {
    setSelectedItem({
      id: item.id,
      code: item.code,
      description: item.description,
      category: item.category,
      value: item.value,
      serialNumber,
    });
    setIsModalOpen(true);
  };

  const onCloseHandler = () => {
    setSelectedItem(undefined);
    setIsModalOpen(false);
    dispatch(
      qalTrackEngagementAction({
        eventAction: 'modal: close',
        eventLabel: 'add item',
        data: {},
      })
    );
  };

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.coverDetailsPortableItems',
    tokens: {
      specifiedItemLimit: () => currencyFormatter(specifiedItemLimit - 1, CurrencyFormat.Comma),
    },
  });
  return (
    <>
      <Typography
        variant="subtitle2"
        component="h3"
        fontWeight="fontWeightBold"
        sx={{ marginBottom: 1 }}
      >
        {getTextContent('content.specifiedItems.title')}
      </Typography>

      <Typography align="center" fontWeight="fontWeightMedium" sx={{ marginBottom: 3 }}>
        {getJSXContent('content.specifiedItems.description')}
      </Typography>

      {specifiedItems && (
        <Box
          sx={{
            mt: 2,
            mb: 2,
            maxWidth: '400px',
            width: '100%',
          }}
        >
          <SelectedPortableItem
            coverList={specifiedItems}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </Box>
      )}

      {specifiedItems?.length !== TOTAL_SPECIFIED_ITEMS_LIMIT && (
        <LinkButton
          label={getTextContent('buttons.addItems')}
          disabled={isMaxLimitError}
          onClick={() => {
            setIsModalOpen(true);
            handleFormStart();
          }}
          muiIcon={AddCircleIcon}
        />
      )}

      {isModalOpen && (
        <UpdateSpecifiedItemModal
          serialNumber={serialNumber}
          selectedItem={selectedItem}
          onUpdate={updateSpecifiedItems}
          onClose={onCloseHandler}
        />
      )}
    </>
  );
};

export default SpecifiedItems;
